import type { ReactNode } from 'react';
import { App as AntApp, ConfigProvider } from 'antd';
import viVN from 'antd/locale/vi_VN';
import { getColors } from '@ai-content-hub/shared-styles';
import { UIProvider } from '@ai-content-hub/ui/providers';
import { UIColorsProvider, createAntdTheme } from '@ai-content-hub/ui/theme';
import { useTheme } from '@/theme/useTheme';

/**
 * EAA-1804: mounts everything `@ai-content-hub/ui` needs, once, at the shell.
 *
 * The portal now consumes the shared kit directly instead of keeping native
 * twins of it. That reverses the earlier "standalone repo, no `@ai-content-hub/*`"
 * stance recorded in PLAN-SSO-OIDC-Backend.md §8 — worth knowing the cost:
 * this repo resolves the kit through the monorepo's pnpm workspace, so a
 * standalone `git clone emg-portal-fe && pnpm install` no longer builds. It
 * only builds checked out as `apps/portal` inside `emg-ai-content-hub-fe`.
 * §8's own answer to that was a versioned artifact via the A4.2 pipeline; if
 * this repo ever needs to build on its own again, that is the path back.
 *
 * Three seams the kit reads from, all mounted here:
 *   - antd `ConfigProvider theme` — the component sheet, via `createAntdTheme`.
 *     Outermost so the locale provider inside inherits it.
 *   - `UIColorsProvider` — the `useUIColors()` seam. Fed the same palette the
 *     CSS custom properties in `tokens.css` carry, so kit components and the
 *     portal's own stylesheets cannot disagree.
 *   - `UIProvider` — the `useUIText()` catalog, pinned to Vietnamese (NFR-08).
 *
 * Deliberately NOT mounted: `AuthCapabilityProvider`. Its default fails closed,
 * and no portal call site passes `auth` — the portal gates by *absence* (a
 * permission-less nav item is not rendered at all), which is what the design
 * sheet prescribes. A future call site that does pass `auth` will correctly
 * render denied until a real capability source is wired in here.
 */
export function UiKitProvider({ children }: { children: ReactNode }) {
    // EAA-1844: follows the real theme now — <ThemeProvider> mounts above this
    // component (see main.tsx) specifically so this seam could read it instead
    // of the EAA-1778 hardcoded `getColors(false)`.
    const { theme } = useTheme();
    const colors = getColors(theme === 'dark');

    return (
        <ConfigProvider theme={createAntdTheme(colors)}>
            <UIColorsProvider colors={colors}>
                <UIProvider locale="vi" antdLocale={viVN}>
                    {/* antd's <App> is what the kit's `useToast` reads through
                        `App.useApp()`. It replaces the portal's own
                        <ToastProvider>; without it the hook silently degrades
                        to unthemed static toasts rendered outside this
                        ConfigProvider, which is a failure mode with no visible
                        error — hence mounting it here rather than per screen. */}
                    <AntApp component={false}>{children}</AntApp>
                </UIProvider>
            </UIColorsProvider>
        </ConfigProvider>
    );
}
